import {
  goalInboxID,
  ID,
  ReorderItem,
  ReorderResult,
  Task,
  TaskListItemTypes,
} from '@griply/types';
import cloneDeep from 'lodash/cloneDeep';

import { applyReorderTaskLinks } from './apply-reorder-task-links';
import { filterReorderableChildIds } from './filter-reorderable-child-ids';
import { findParentItem } from './find-parent-item';

export type OnGoalReorderOptions = {
  updatedItems: readonly ReorderItem[];
  oldItems: readonly ReorderItem[];
  tasksMap: Record<ID, Task>;
  rootSectionId: ID;
};

export const onGoalReorder = ({
  updatedItems,
  oldItems,
  tasksMap,
  rootSectionId,
}: OnGoalReorderOptions): ReorderResult => {
  const updatedTasks: Task[] = [];
  const updatedSections: ReorderResult['updatedSections'] = [];

  const goalIds = new Set(
    [...oldItems, ...updatedItems]
      .filter((item) => item.type === TaskListItemTypes.Goal)
      .map(({ id }) => id),
  );

  updatedItems.forEach((item) => {
    if (item.type === TaskListItemTypes.Task) {
      if (!tasksMap[item.id]) {
        return;
      }

      const taskToUpdate = cloneDeep(tasksMap[item.id]);
      const parentItem =
        findParentItem(updatedItems, item.id) ??
        findParentItem(oldItems, item.id);

      const goalId =
        !parentItem?.id || parentItem.id === goalInboxID ? null : parentItem.id;

      applyReorderTaskLinks({
        task: taskToUpdate,
        original: tasksMap[item.id],
        goalIds: goalId ? [goalId] : [],
        ...(goalId ? { lifeAreaIds: [] } : {}),
      });

      updatedTasks.push(taskToUpdate);
      return;
    }

    if (item.type === TaskListItemTypes.Root) {
      updatedSections.push({
        id: rootSectionId,
        taskSorting: filterReorderableChildIds(item.children),
      });
      return;
    }

    if (item.type === TaskListItemTypes.Goal) {
      const childIds = filterReorderableChildIds(item.children);
      const goalSorting = childIds.filter((id) => goalIds.has(id));
      updatedSections.push({
        id: item.id,
        taskSorting: childIds.filter((id) => !goalIds.has(id)),
        ...(goalSorting.length ? { goalSorting } : {}),
      });
    }
  });

  return { updatedSections, updatedTasks };
};
